import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { checkStripeConnectStatus, getStripeConnectRequirements } from '../utils/stripeConnectStatus';
import { getStripeConnectStatusFromFunction } from '../utils/stripeConnectStatus';

const StripeConnectPrompt = ({ userData, onDismiss }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const status = checkStripeConnectStatus(userData);
  const requirements = getStripeConnectRequirements(userData?.role);

  const [fetched, setFetched] = useState(false);
  const [remoteActive, setRemoteActive] = useState(false);
  const [remoteError, setRemoteError] = useState('');

  // Fetch authoritative status from backend to avoid flicker
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const result = await getStripeConnectStatusFromFunction();
        if (cancelled) return;
        // Consider active only when all are enabled
        const active = !!(result?.hasAccount && result?.chargesEnabled && result?.payoutsEnabled);
        setRemoteActive(active);
      } catch (e) {
        // If call fails, don't block UI — fall back to local heuristic
        setRemoteError('');
      } finally {
        if (!cancelled) setFetched(true);
      }
    };
    // Only fetch when user present and requirement could apply
    if (user && requirements.required) {
      fetchStatus();
    } else {
      setFetched(true);
    }
    return () => {
      cancelled = true;
    };
  }, [user, requirements.required]);

  // Hide until fetched to avoid flicker
  if (!fetched) {
    return null;
  }

  // Don't show prompt if not needed or already active per backend
  if (!requirements.required || status.isSetUp || remoteActive) {
    return null;
  }

  const handleSetup = () => {
    navigate('/stripe-connect-onboarding');
  };

  const handleSkip = async () => {
    if (!requirements.canSkip) {
      setError('Payment setup is required for your account type');
      return;
    }

    setLoading(true);
    try {
      await setDoc(doc(db, 'users', user.uid), {
        stripeConnectSkipped: true,
        stripeConnectSkippedAt: new Date()
      }, { merge: true });

      if (onDismiss) {
        onDismiss();
      }
    } catch (error) {
      console.error('Error skipping Stripe Connect:', error);
      setError('Failed to skip setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-6 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-200">
            Payment Account Setup Required
          </h3>
          <div className="mt-2 text-sm text-yellow-200">
            <p>{requirements.reason}</p>
            <p className="mt-1">
              Set up your payment account to launch games and receive payments for marketplace sales.
            </p>
          </div>
          {error && (
            <div className="mt-2 text-sm text-red-200">
              {error}
            </div>
          )}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSetup}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium px-4 py-2 rounded-lg text-sm"
            >
              Set Up Payment Account
            </button>
            {requirements.canSkip && (
              <button
                onClick={handleSkip}
                disabled={loading}
                className="text-yellow-200 hover:text-white font-medium px-4 py-2 rounded-lg text-sm border border-yellow-600 hover:border-yellow-500 disabled:opacity-50"
              >
                {loading ? 'Skipping...' : 'Skip for Now'}
              </button>
            )}
          </div>
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={onDismiss}
            className="text-yellow-400 hover:text-yellow-300"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectPrompt;
